import { useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Button } from "@components/Button"
import useRQAdLaunches from "@hooks/useRQAdLaunches"
import { notify } from "@utils/notify"
import { LoaderCircle } from "lucide-react"

const AdsPostingProgress = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { batchId, quick_launch_id, isScheduled, scheduledTime, launchInMinutes, displayScheduledTime } = location.state || {}
  const userId = localStorage.getItem("userId")

  const [progress, setProgress] = useState(0)
  const [isComplete, setIsComplete] = useState(false)
  const [failedJobs, setFailedJobs] = useState([])
  const [jobIds, setJobIds] = useState([])
  const [adLaunchObjects, setAdLaunchObjects] = useState([])
  const [isLaunching, setIsLaunching] = useState(true)
  const [totalJobs, setTotalJobs] = useState(0)
  const [completedJobs, setCompletedJobs] = useState(0)

  const {
    handleFBAdsForPosting,
    handleGetMultipleJobsStatus,
    handleFetchFBAdLaunchLogs
  } = useRQAdLaunches()

  useEffect(() => {
    const launchAds = async () => {
      if (!batchId || !userId) {
        notify("Missing batch ID or user ID", "error")
        // navigate(-1) // Go back if no batchId or userId
        return
      }
      try {
        setIsLaunching(true)
        let response
        if (quick_launch_id) {
          response = await handleFBAdsForPosting(batchId, false, quick_launch_id)
        } else {
          response = await handleFBAdsForPosting(batchId)
        }

        if (!response || !response.job_ids) {
          throw new Error("Failed to launch ads")
        }

        localStorage.removeItem("publishPayloadFromAdvMapping")
        localStorage.removeItem("existingAdAdsetMapping")

        if (isScheduled && scheduledTime && launchInMinutes && displayScheduledTime) {
          setIsLaunching(false)
          navigate("/launch-history", {
            state: {
              scheduledBatchId: batchId,
              isScheduled: isScheduled,
              scheduledTime: scheduledTime,
              displayScheduledTime: displayScheduledTime
            },
          });
        }

        if (quick_launch_id) {
          navigate("/launch-history", {});
        }

        setJobIds(response.job_ids)
        setAdLaunchObjects(response.fb_ad_launch_objects || [])
        setTotalJobs(response.job_ids.length)
        setIsLaunching(false)
      } catch (error) {
        console.error("Error launching ads:", error)
        notify("Error launching ads", "error")
        setIsLaunching(false)
      }
    }
    launchAds()
  }, [batchId, userId, isScheduled, scheduledTime, launchInMinutes])

  // Step 3: Poll for job status every 15 seconds
  useEffect(() => {
    let intervalId

    const checkJobsStatus = async () => {
      if (!jobIds.length || isComplete) return

      try {
        if (isLaunching) {
          return
        }
        const response = await handleGetMultipleJobsStatus(jobIds)

        if (!response) {
          return
        }
        let completed = 0
        let failed = []

        Object.entries(response).forEach(([jobId, status]) => {
          if (
            status === "finished" ||
            status === "error" ||
            status === "failed"
          ) {
            completed++

            if (status === "error" || status === "failed") {
              // Find the ad object that corresponds to this job
              const failedAd = adLaunchObjects.find(
                (obj) => obj.job_id === jobId
              )
              if (failedAd) {
                failed.push({
                  id: failedAd.adId,
                  name: `Ad ID: ${failedAd.adId}`,
                  error:
                    status === "error"
                      ? "Error posting ad"
                      : "Failed to post ad",
                  jobId,
                  launchId: failedAd.launchId,
                })
              }
            }
          }
        })

        setCompletedJobs(completed)
        setFailedJobs((current) => {
          // Only add new failures that aren't already in the list
          const existingJobIds = current.map((job) => job.jobId)
          const newFailures = failed.filter(
            (job) => !existingJobIds.includes(job.jobId)
          )
          return [...current, ...newFailures]
        })

        const newProgress =
          totalJobs > 0 ? Math.floor((completed / totalJobs) * 100) : 0
        setProgress(newProgress)

        if (completed === totalJobs) {
          setIsComplete(true)
          clearInterval(intervalId)

          // Fetch logs for completed jobs (dummy implementation for now)
          fetchAdLogs()
        }
      } catch (error) {
        console.error("Error checking job status:", error)
      }
    }

    if (jobIds.length && !isComplete) {
      // Initial check right away
      // TODO: change to 15 seconds
      checkJobsStatus()
      intervalId = setInterval(checkJobsStatus, 10000)
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [jobIds, isComplete, totalJobs, adLaunchObjects])

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      // Show a confirmation dialog
      event.preventDefault()
      event.returnValue = "" // Required for modern browsers to show the dialog
    }

    // Add the event listener
    window.addEventListener("beforeunload", handleBeforeUnload)

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload)
    }
  }, [])

  const fetchAdLogs = async () => {
    try {
      const response = await handleFetchFBAdLaunchLogs(batchId)
      if (!response || !response.status_objs) {
        console.error("Invalid logs response:", response)
        return
      }

      // Process the logs response
      const failedAdsFromLogs = response.status_objs
        .filter((ad) => ad.status !== "completed" || ad.fbErrorLogs)
        .map((ad) => ({
          id: ad.launchId,
          name: ad.launchId,
          error: ad.fbErrorLogs || "Failed to post ad",
          launchId: ad.launchId,
          status: ad.status,
        }))

      if (failedAdsFromLogs.length > 0) {
        setFailedJobs(failedAdsFromLogs)
      } else if (failedJobs.length === 0) {
        // If we found no failures in logs and had none before, everything is successful
        console.log("All ads posted successfully according to logs")
      }
    } catch (error) {
      console.error("Error fetching ad logs:", error)
    }
  }

  const handleComplete = () => {
    if (quick_launch_id) {
      navigate("/quick-launch-ads")
    }
    else {
      navigate("/launch-new-ad")
    }
  }

  const handleLaunchButtonClick = () => {
    if (quick_launch_id) {
      navigate("/quick-launch-ads")
    }
    else {
      navigate("/launch-new-ad")
    }
  }

  const handleRetryAll = async () => {
    setFailedJobs([])
    setIsComplete(false)
    setIsLaunching(true)
    const retryFailedAds = true
    let response
    if (quick_launch_id) {
      response = await handleFBAdsForPosting(batchId, retryFailedAds, quick_launch_id)
    } else {
      response = await handleFBAdsForPosting(batchId, retryFailedAds)
    }

    if (!response || !response.job_ids) {
      throw new Error("Failed to launch ads")
    }
    setJobIds(response.job_ids)
    setAdLaunchObjects(response.fb_ad_launch_objects || [])
    setTotalJobs(response.job_ids.length)
    setIsLaunching(false)
  }

  return (
    <div className="flex flex-col justify-center items-center min-h-[calc(100vh-64px)] p-8">
      <div className="max-w-[800px] w-full text-center bg-white rounded-xl shadow-[0_0_15px_rgba(0,0,0,0.05)] border border-gray-100 overflow-hidden p-8">
        <div className="flex flex-col items-center mb-8">
          <h1 className="text-4xl font-bold text-transparent bg-gradient-to-r from-indigo-600 to-blue-500 bg-clip-text">
            Posting Ads to Meta
          </h1>
          <div className="w-full h-[1px] bg-gray-200 mt-6"></div>
        </div>

        {isLaunching ? (
          <div className="my-8 text-center">
            <div className="inline-block w-8 h-8 mb-4 border-b-2 border-indigo-600 rounded-full animate-spin"></div>
            <p className="text-gray-600">Preparing to launch ads...</p>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Progress: {progress}%</span>
                <span className="text-gray-600">
                  {completedJobs}/{totalJobs} Ads
                </span>
              </div>
              <div className="w-full h-4 mb-4 bg-gray-200 rounded-full">
                <div
                  className="h-4 transition-all duration-300 bg-indigo-600 rounded-full"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>

            {!isComplete && (
              <div className="p-4 my-6 border border-indigo-100 rounded-lg bg-indigo-50">
                <LoaderCircle className="w-4 h-4 mx-auto text-indigo-600 animate-spin" />
                <p className="mt-3 font-medium text-indigo-800">
                  Processing your ads
                </p>
                {/* <p className="mt-1 text-sm text-indigo-600/80">
                  Please keep this window open while we complete the process
                </p> */}
              </div>
            )}

            {isComplete && (
              <div className="mt-8">
                {failedJobs.length === 0 ? (
                  <div className="mb-4 text-green-600">
                    <svg
                      className="w-12 h-12 mx-auto mb-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    <p className="text-xl">All ads posted successfully!</p>
                  </div>
                ) : (
                  <div className="mb-4 text-yellow-600">
                    <svg
                      className="w-12 h-12 mx-auto mb-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    <p className="text-xl">
                      {failedJobs.length} ads failed to post. See details below.
                    </p>
                  </div>
                )}

                <div className="flex justify-center gap-4 mt-8">
                  {failedJobs.length === 0 ? null : (
                    <Button
                      variant="primary"
                      size="lg"
                      text="Retry All"
                      className="text-white bg-indigo-600 hover:bg-indigo-700"
                      onClick={handleRetryAll}
                    />
                  )}
                  <Button
                    variant="primary"
                    size="lg"
                    text="Continue"
                    className="text-white bg-indigo-600 hover:bg-indigo-700"
                    onClick={handleComplete}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {failedJobs.length > 0 && isComplete && (
          <div className="mt-8 text-left">
            <h2 className="mb-4 text-xl text-left">Failed Ads</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                    >
                      Ad ID
                    </th>
                    <th
                      scope="col"
                      className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase"
                    >
                      Error Description
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {failedJobs.map((job) => (
                    <tr key={job.id}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap">
                        {job.name}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                        {job.error}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <button
        className="px-6 py-2 mt-4 font-semibold text-white transition-colors duration-200 bg-blue-600 rounded-lg shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75"
        onClick={handleLaunchButtonClick}
      >
        Launch More Ads
      </button>
    </div>
  )
}

export default AdsPostingProgress
